// Chakra imports
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import config from "config";
import React, { useState } from "react";
import { FiMail, FiSend, FiClock, FiShield, FiUserCheck } from "react-icons/fi";
import { FaPaperPlane } from "react-icons/fa";
import { getToken } from "utils/auth";

const Invite = ({ title, setInvitations }) => {
  const [invitationMail, setInvitationMail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [touched, setTouched] = useState(false);

  const toast = useToast();

  const textColor = useColorModeValue("gray.700", "white");
  const subTextColor = useColorModeValue("gray.500", "gray.400");
  const inputBg = useColorModeValue("gray.50", "navy.800");
  const chipBg = useColorModeValue("gray.50", "navy.900");
  const chipBorder = useColorModeValue("gray.200", "whiteAlpha.100");
  const iconBg = useColorModeValue("teal.50", "navy.700");

  const validateEmail = (val) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(val).trim().toLowerCase());
  };

  const isEmailValid = validateEmail(invitationMail);
  const hasError = touched && invitationMail.length > 0 && !isEmailValid;

  const showNotification = (notificationTitle, description, status) => {
    toast({
      title: notificationTitle,
      description: description,
      status: status,
      duration: 5000,
      isClosable: true,
      position: "top-right",
    });
  };

  const handleInvite = async (e) => {
    if (e) e.preventDefault();
    setTouched(true);

    const emailToSend = invitationMail.trim();
    if (!emailToSend || !validateEmail(emailToSend)) {
      showNotification("Invalid Email", "Please provide a valid email address.", "warning");
      return;
    }

    setIsSending(true);
    const token = getToken();

    try {
      const response = await axios.post(
        `${config.API_URL}/invitation/send`,
        {
          email: emailToSend,
          url: config.URL,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const res = await axios.get(`${config.API_URL}/invitation/invitations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvitations(res.data);

      showNotification(
        "Invitation Dispatched",
        `An official invitation has been sent to ${emailToSend}.`,
        "success"
      );
      setInvitationMail("");
      setTouched(false);
    } catch (error) {
      console.error("Error sending invitation:", error);
      const serverMsg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        `Error sending invitation to ${emailToSend}.`;
      showNotification("Invitation Failed", serverMsg, "error");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Card p={{ base: "20px", md: "26px" }} mb="24px" w="100%" boxShadow="0px 3.5px 20px rgba(0, 0, 0, 0.04)">
      <CardHeader p="0px" mb="18px">
        <Flex align="center" gap="14px">
          <Flex
            w="48px"
            h="48px"
            borderRadius="14px"
            bg={iconBg}
            color="teal.400"
            align="center"
            justify="center"
            boxShadow="sm"
          >
            <Icon as={FaPaperPlane} w="22px" h="22px" />
          </Flex>
          <Box>
            <Text fontSize={{ base: "lg", md: "xl" }} color={textColor} fontWeight="bold">
              {title || "Invite Collaborators"}
            </Text>
            <Text fontSize="sm" color={subTextColor}>
              Send a secure invitation link to researchers, clinicians, or lab partners to join the platform.
            </Text>
          </Box>
        </Flex>
      </CardHeader>

      <CardBody flexDirection="column" w="100%" p="0px">
        <Box as="form" onSubmit={handleInvite} w="100%" mb="8px">
          <FormControl isInvalid={hasError} w="100%">
            <Flex
              direction={{ base: "column", md: "row" }}
              gap="12px"
              w="100%"
              align={{ base: "stretch", md: "center" }}
            >
              <InputGroup size="lg" flex="1">
                <InputLeftElement pointerEvents="none" h="100%">
                  <Icon as={FiMail} color="gray.400" w="18px" h="18px" />
                </InputLeftElement>
                <Input
                  borderRadius="12px"
                  fontSize="sm"
                  bg={inputBg}
                  border="1px solid"
                  borderColor={useColorModeValue("gray.200", "whiteAlpha.200")}
                  type="email"
                  placeholder="colleague@institution.tn or name@domain.com"
                  value={invitationMail}
                  onChange={(e) => {
                    setInvitationMail(e.target.value);
                    if (!touched) setTouched(true);
                  }}
                  _focus={{
                    borderColor: "teal.400",
                    boxShadow: "0 0 0 1px #319795",
                  }}
                />
              </InputGroup>

              <Button
                size="lg"
                type="submit"
                colorScheme="teal"
                minW={{ base: "100%", md: "180px" }}
                px="24px"
                borderRadius="12px"
                isLoading={isSending}
                loadingText="Sending..."
                isDisabled={!invitationMail.trim() || (touched && !isEmailValid)}
                leftIcon={<Icon as={FiSend} />}
                fontSize="sm"
                fontWeight="bold"
                boxShadow="0 4px 12px rgba(49, 151, 149, 0.25)"
                _hover={{
                  boxShadow: "0 6px 16px rgba(49, 151, 149, 0.35)",
                  transform: "translateY(-1px)",
                }}
                _active={{
                  transform: "translateY(0px)",
                }}
              >
                Send Invitation
              </Button>
            </Flex>
            {hasError && (
              <FormErrorMessage mt="8px" fontSize="xs">
                Please enter a valid email address format (e.g. user@domain.com).
              </FormErrorMessage>
            )}
          </FormControl>
        </Box>

        {/* Informational Badges / Chips */}
        <Flex
          direction={{ base: "column", md: "row" }}
          w="100%"
          gap="12px"
          pt="16px"
          mt="8px"
          borderTop="1px solid"
          borderColor={useColorModeValue("gray.100", "whiteAlpha.100")}
        >
          <Flex
            align="center"
            gap="10px"
            bg={chipBg}
            border="1px solid"
            borderColor={chipBorder}
            borderRadius="10px"
            px="14px"
            py="10px"
            flex="1"
          >
            <Icon as={FiClock} color="orange.400" w="18px" h="18px" />
            <Box>
              <Text fontSize="xs" fontWeight="bold" color={textColor}>
                72-Hour Validity
              </Text>
              <Text fontSize="11px" color={subTextColor}>
                Links expire automatically after 3 days
              </Text>
            </Box>
          </Flex>

          <Flex
            align="center"
            gap="10px"
            bg={chipBg}
            border="1px solid"
            borderColor={chipBorder}
            borderRadius="10px"
            px="14px"
            py="10px"
            flex="1"
          >
            <Icon as={FiShield} color="teal.400" w="18px" h="18px" />
            <Box>
              <Text fontSize="xs" fontWeight="bold" color={textColor}>
                Secure Token
              </Text>
              <Text fontSize="11px" color={subTextColor}>
                Encrypted single-use registration URL
              </Text>
            </Box>
          </Flex>

          <Flex
            align="center"
            gap="10px"
            bg={chipBg}
            border="1px solid"
            borderColor={chipBorder}
            borderRadius="10px"
            px="14px"
            py="10px"
            flex="1"
          >
            <Icon as={FiUserCheck} color="blue.400" w="18px" h="18px" />
            <Box>
              <Text fontSize="xs" fontWeight="bold" color={textColor}>
                Researcher Status
              </Text>
              <Text fontSize="11px" color={subTextColor}>
                Auto-assigned access to public records
              </Text>
            </Box>
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  );
};

export default Invite;
